/**
 * useBeta - Consolidated beta hooks
 * Includes: beta access check + beta monitor dashboard
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { subDays, subHours } from "date-fns";
import { coreWebVitals } from "@/lib/performanceBudgets";

// ============================================================
// BETA ACCESS (cached via React Query)
// ============================================================

interface BetaAccessState {
  isLoading: boolean;
  hasBetaAccess: boolean;
  betaStatus: 'none' | 'waitlist' | 'invited' | 'active';
}

async function fetchBetaAccess(userId: string, email: string | undefined): Promise<Omit<BetaAccessState, 'isLoading'>> {
  const { data: inviteById } = await supabase
    .from('beta_invites')
    .select('status')
    .eq('user_id', userId)
    .maybeSingle();

  if (inviteById) {
    return {
      hasBetaAccess: inviteById.status === 'active',
      betaStatus: inviteById.status as BetaAccessState['betaStatus'],
    };
  }

  if (email) {
    const { data: inviteByEmail } = await supabase
      .from('beta_invites')
      .select('id, status')
      .eq('email', email)
      .maybeSingle();

    if (inviteByEmail) {
      if (inviteByEmail.status === 'active' || inviteByEmail.status === 'invited') {
        await supabase
          .from('beta_invites')
          .update({ user_id: userId })
          .eq('id', inviteByEmail.id)
          .is('user_id', null);
      }
      return {
        hasBetaAccess: inviteByEmail.status === 'active',
        betaStatus: inviteByEmail.status as BetaAccessState['betaStatus'],
      };
    }

    const { data: waitlist } = await supabase
      .from('beta_waitlist')
      .select('status')
      .eq('email', email)
      .maybeSingle();

    if (waitlist) {
      return {
        hasBetaAccess: false,
        betaStatus: waitlist.status === 'invited' ? 'invited' : 'waitlist',
      };
    }
  }

  return { hasBetaAccess: false, betaStatus: 'none' };
}

export function useBetaAccess(): BetaAccessState {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['beta-access', user?.id],
    queryFn: () => fetchBetaAccess(user!.id, user!.email ?? undefined),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  if (!user) {
    return { isLoading: false, hasBetaAccess: false, betaStatus: 'none' };
  }

  return {
    isLoading,
    hasBetaAccess: data?.hasBetaAccess ?? false,
    betaStatus: data?.betaStatus ?? 'none',
  };
}

// ============================================================
// BETA MONITOR (admin dashboard)
// ============================================================

const CATEGORY_EMOJIS: Record<string, string> = {
  confused: "😕",
  bug: "🐛",
  suggestion: "💡",
  praise: "🎉",
  general: "💬",
  unknown: "❓",
};

interface BetaMonitorData {
  // KPIs
  feedback24h: number;
  errorRate: number;
  uptime: number;
  activeBetaUsers: number;
  // Charts
  frictionBreakdown: { category: string; count: number; emoji: string }[];
  feedbackByPage: { page: string; count: number }[];
  // Recent feedback
  recentFeedback: {
    id: string;
    category: string;
    feedback: string;
    page_url: string | null;
    created_at: string;
    user_id: string | null;
  }[];
  // Web vitals
  webVitals: {
    metric: string;
    avg: number;
    target: number;
    rating: "good" | "needs-improvement" | "poor";
  }[];
}

export function useBetaMonitor() {
  const d7Ago = subDays(new Date(), 7).toISOString();
  const h24Ago = subHours(new Date(), 24).toISOString();

  return useQuery<BetaMonitorData>({
    queryKey: ["beta-monitor"],
    queryFn: async () => {
      const [
        feedbackRes24h,
        metricsRes,
        invitesRes,
        categoriesRes,
        pagesRes,
        recentRes,
        vitalsRes,
      ] = await Promise.all([
        supabase.from("beta_feedback").select("id", { count: "exact", head: true }).gte("created_at", h24Ago),
        supabase.from("api_metrics").select("status_code").gte("created_at", h24Ago),
        supabase.from("beta_invites").select("id", { count: "exact", head: true }).not("activated_at", "is", null),
        supabase.from("beta_feedback").select("category").gte("created_at", d7Ago),
        supabase.from("beta_feedback").select("page_url").gte("created_at", d7Ago).not("page_url", "is", null),
        supabase.from("beta_feedback").select("id, category, feedback, page_url, created_at, user_id").order("created_at", { ascending: false }).limit(20),
        supabase.from("web_vitals").select("metric_name, metric_value").gte("created_at", h24Ago),
      ]);

      const feedback24h = feedbackRes24h.count ?? 0;
      const activeBetaUsers = invitesRes.count ?? 0;

      // Error rate & uptime
      const metrics = metricsRes.data ?? [];
      const errorCount = metrics.filter((m: { status_code: number }) => m.status_code >= 500).length;
      const errorRate = metrics.length > 0 ? Math.round((errorCount / metrics.length) * 10000) / 100 : 0;
      const uptime = Math.max(0, 100 - errorRate);

      // Friction breakdown by category
      const catCounts: Record<string, number> = {};
      for (const row of categoriesRes.data ?? []) {
        const cat = (row as { category: string }).category || "unknown";
        catCounts[cat] = (catCounts[cat] || 0) + 1;
      }
      const frictionBreakdown = Object.entries(catCounts)
        .map(([category, count]) => ({ category, count, emoji: CATEGORY_EMOJIS[category] || "❓" }))
        .sort((a, b) => b.count - a.count);

      // Feedback by page
      const pageCounts: Record<string, number> = {};
      for (const row of pagesRes.data ?? []) {
        const page = (row as { page_url: string }).page_url || "unknown";
        pageCounts[page] = (pageCounts[page] || 0) + 1;
      }
      const feedbackByPage = Object.entries(pageCounts)
        .map(([page, count]) => ({ page, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Recent feedback
      const recentFeedback = (recentRes.data ?? []).map((r) => ({
        id: (r as Record<string, string>).id,
        category: (r as Record<string, string>).category,
        feedback: (r as Record<string, string>).feedback,
        page_url: (r as Record<string, string | null>).page_url,
        created_at: (r as Record<string, string>).created_at,
        user_id: (r as Record<string, string | null>).user_id,
      }));

      // Web vitals averages
      const vitalsMap: Record<string, number[]> = {};
      for (const row of vitalsRes.data ?? []) {
        const r = row as { metric_name: string; metric_value: number };
        if (!vitalsMap[r.metric_name]) vitalsMap[r.metric_name] = [];
        vitalsMap[r.metric_name].push(r.metric_value);
      }

      const targetMap: Record<string, number> = {
        LCP: coreWebVitals.LCP,
        FCP: coreWebVitals.FCP,
        CLS: coreWebVitals.CLS,
        FID: coreWebVitals.FID,
        TTFB: coreWebVitals.TTFB,
        INP: coreWebVitals.INP,
      };

      const webVitals = Object.entries(vitalsMap).map(([metric, values]) => {
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        const target = targetMap[metric] ?? 1000;
        const ratio = avg / target;
        const rating: "good" | "needs-improvement" | "poor" = ratio <= 1 ? "good" : ratio <= 2 ? "needs-improvement" : "poor";
        return { metric, avg: Math.round(avg * 10) / 10, target, rating };
      });

      // Ensure we always show the standard metrics even without data
      for (const [metric, target] of Object.entries(targetMap)) {
        if (!webVitals.find((v) => v.metric === metric)) {
          webVitals.push({ metric, avg: 0, target, rating: "good" });
        }
      }

      return {
        feedback24h,
        errorRate,
        uptime: Math.round(uptime * 100) / 100,
        activeBetaUsers,
        frictionBreakdown,
        feedbackByPage,
        recentFeedback,
        webVitals,
      };
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}
